import { Box, Container, Grid } from "@mui/material";
import BottomSpacedPageHeader from "components/common/ui/BottomSpacedPageHeader";
import CommonButton from "components/common/ui/CommonButton";
import EditSection from "components/common/ui/EditSection";
import React, {
  ChangeEvent,
  FC,
  useCallback,
  useEffect,
  useState,
} from "react";
import ColorCircularProgress from "components/common/ui/ColorCircularProgress";
import styled, { css } from "styled-components";
import { usePromptSnackBar } from "components/common/ui/PromptSnackBarProvider";
import AdaptiveRowHeader from "components/common/ui/AdaptiveRowHeader";
import Typography from "@mui/material/Typography";
import { useAppDispatch, useAppSelector } from "hooks/useReduxStore";
import { actions as foodItemOptionsActions } from "store/slices/food/foodItemOptionsSlice";
import foodItemOptionsSelector from "store/selectors/food/foodItemOptionsSelector";
import foodTagOptionsSelector from "store/selectors/food/foodTagOptionsSelector";
import { actions as foodTagOptionsActions } from "store/slices/food/foodTagOptionsSlice";
import invoiceUpdateSupplierInfoSelector from "store/selectors/foodItem/invoiceUpdateSupplierInfoSelector";
import GFSInvoiceItemActionBox from "components/food/ui/supplierInfo/invoiceUpdateSupplier/GFSInvoiceItemActionBox";
import { invoiceUpdateSupplierInfoActions } from "store/slices/foodItem/invoiceUpdateSupplierInfoSlice";
// eslint-disable-next-line max-len
import InvoiceUpdateSupplierInfoSummaryBox from "components/food/ui/supplierInfo/invoiceUpdateSupplier/InvoiceUpdateSupplierInfoSummaryBox";
import { SupplierInfoActionStatus } from "models/food/supplierInfo/actionStatus";

const ProgressPromptField = styled(Typography)`
  font-size: 1rem;
  font-weight: 500;
  padding: 0 0 0 2rem;
  color: rgb(120, 203, 255);
`;

enum fileUploadingState {
  notUploaded = "notUploaded",
  uploaded = "uploaded",
}

interface RefreshBySupplierInvoiceCSVPageProps {}

const InvoiceUpdateSupplierInfoPage: FC<
  RefreshBySupplierInvoiceCSVPageProps
> = () => {
  const dispatch = useAppDispatch();
  const promptSnackBar = usePromptSnackBar();
  const [showMatchedInvoiceDetails, setShowMatchedInvoiceDetails] =
    useState<boolean>(false);
  const [file, setFile] = useState<File | null>(null);
  const [fileState, setFileState] = useState<fileUploadingState>(
    fileUploadingState.notUploaded,
  );
  const toSolvedItems = useAppSelector(
    invoiceUpdateSupplierInfoSelector.invoiceUpdateSupplierInfoActionItems,
  );
  const solvedInvoices = useAppSelector(
    invoiceUpdateSupplierInfoSelector.invoiceUpdateSupplierInfoResolvedInvoices,
  );
  const summary = useAppSelector(invoiceUpdateSupplierInfoSelector.summary);
  const isFileUploading = useAppSelector(
    invoiceUpdateSupplierInfoSelector.invoiceUpdateSupplierInfoIsLoading,
  );
  const ingredientOptions = useAppSelector(
    foodItemOptionsSelector.foodIngredientOptions,
  );
  const foodTagOptions = useAppSelector(foodTagOptionsSelector.foodTagOptions);

  /**
   * Use effect to load the food tag option when first time loading the page
   */
  useEffect(() => {
    if (foodTagOptions.length === 0) {
      dispatch(foodTagOptionsActions.fetchAllFoodTagOptions());
    }
  }, [dispatch, foodTagOptions.length]);

  /**
   * Make sure FoodItemOptions is the latest
   */
  useEffect(() => {
    dispatch(foodItemOptionsActions.fetchAllFoodItemOptions());
  }, [dispatch]);

  const handleFileChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFile(e.target.files[0]);
      setFileState(fileUploadingState.notUploaded);
    }
  }, []);

  const handleFileUploadClick = useCallback(() => {
    if (file) {
      dispatch(invoiceUpdateSupplierInfoActions.init(null));
      dispatch(
        invoiceUpdateSupplierInfoActions.postInvoiceCSVFile({
          invoiceCSVFile: file,
          supplierId: "GFS",
        }),
      )
        .unwrap() // Unwrap the result to handle success or failure
        .then(() => {
          setFileState(fileUploadingState.uploaded);
        })
        .catch((err) => {
          promptSnackBar.setPromptProps({
            isOpen: true,
            message: `File uploading failed: ${err}`,
            severity: "error",
          });
          setFileState(fileUploadingState.notUploaded);
        });
    }
  }, [dispatch, file, promptSnackBar]);

  return (
    <Container>
      <BottomSpacedPageHeader>
        Update Supplier Info via Invoice File
      </BottomSpacedPageHeader>
      <EditSection header="Select Invoice CSV file">
        <input id="order-csv-file" type="file" onChange={handleFileChange} />
        {file && (
          <section>
            File details:
            <ul>
              <li>Name: {file.name}</li>
              <li>Type: {file.type}</li>
            </ul>
          </section>
        )}
        {file &&
          fileState === fileUploadingState.notUploaded &&
          !isFileUploading && (
            <CommonButton
              onClick={handleFileUploadClick}
              text="Upload to update"
            />
          )}
      </EditSection>
      {isFileUploading ? (
        <Grid container columns={2}>
          <AdaptiveRowHeader switchWidthInPixel={1}>
            <Box sx={{ display: "flex" }}>
              <ColorCircularProgress
                customColor={css`rgb(120, 203, 255)`}
                thickness={20}
              />
            </Box>
            <ProgressPromptField>Analysing ...</ProgressPromptField>
          </AdaptiveRowHeader>
        </Grid>
      ) : null}
      <EditSection header="Invoices with matched code">
        {summary.updated} invoice items have been updated.
        <CommonButton
          onClick={() => {
            setShowMatchedInvoiceDetails(!showMatchedInvoiceDetails);
          }}
          text={`${showMatchedInvoiceDetails ? "Hide" : "Show"} details`}
        />
        {showMatchedInvoiceDetails
          ? Object.entries(solvedInvoices).map(([actionItemKey, invoice]) => (
              <GFSInvoiceItemActionBox
                key={actionItemKey}
                displayKey={actionItemKey}
                data={invoice}
                state={{
                  chooseAction: SupplierInfoActionStatus.AUTO_UPDATED,
                  actionMsg: "Updated supplier info from invoice",
                }}
                ingredientOptions={ingredientOptions}
              />
            ))
          : null}
      </EditSection>
      <EditSection header="Need action items">
        {Object.entries(toSolvedItems).map(([actionItemKey, invoiceItem]) => (
          <GFSInvoiceItemActionBox
            key={actionItemKey}
            displayKey={actionItemKey}
            data={invoiceItem.data}
            state={invoiceItem.state}
            ingredientOptions={ingredientOptions}
          />
        ))}
      </EditSection>
      <EditSection header="Summary">
        <InvoiceUpdateSupplierInfoSummaryBox {...summary} />
      </EditSection>
      <EditSection />
    </Container>
  );
};

export default InvoiceUpdateSupplierInfoPage;
